import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getActiveProjectId, setActiveProjectId as storeActiveProjectId } from '../utils/session';

interface ProjectContextType {
  activeProjectId: string | null;
  setActiveProjectId: (id: string) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(
    () => getActiveProjectId()
  );

  // Stay in sync when AuthContext writes the key after login (cross-tab too)
  useEffect(() => {
    const sync = () => {
      setActiveProjectIdState(getActiveProjectId());
    };
    window.addEventListener('storage', sync);
    // Also re-read on focus in case same-tab auth resolved after mount
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const setActiveProjectId = (id: string) => {
    storeActiveProjectId(id);
    setActiveProjectIdState(id);
  };

  return (
    <ProjectContext.Provider value={{ activeProjectId, setActiveProjectId }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used within a ProjectProvider');
  return ctx;
};
